import * as AuthService from '@/services/auth';
import { resetAllStores } from '@/utils/resetStores.utils';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { ModelUserResponse } from '@layup/api-sdk';

export const useUserStore = defineStore('users', {
  state: () => {
    const user = ref<ModelUserResponse>({});
    const expiresAt = new Date();

    return {
      user,
      expiresAt,
    };
  },
  getters: {
    isAuthenticated(state) {
      return !!state.user && '_id' in state.user && state.expiresAt > new Date();
    },
    hasRequiredPersonalDetails(state) {
      return !!(
        state.user.name &&
        state.user.email &&
        state.user.cellNumber &&
        (state.user.idNumber || state.user.passportNumber)
      );
    },
    hasLoggedInBefore(state) {
      return !!state.user.hasLoggedInBefore;
    },
  },
  actions: {
    async signIn(user: ModelUserResponse) {
      resetAllStores();
      this.user = user;
      const sessionExpiry = await AuthService.getSessionExpiry();
      if (!sessionExpiry || !sessionExpiry.expiresAt) {
        await AuthService.signOut();
        throw new Error('Failed to retrieve session expiry. Unable to Sign In.');
      }
      // Always use the server-provided expiry timestamp
      this.expiresAt = new Date(sessionExpiry.expiresAt);
    },
    async signOut() {
      resetAllStores();
      await AuthService.signOut();
      // We only need to session storage while we have multiple sites. Can be removed once merchant and shopper are merged.
      sessionStorage.clear();
    },
    reset() {
      this.$reset();
    },
    async getSessionExpiry() {
      const sessionExpiry = await AuthService.getSessionExpiry();
      if (!sessionExpiry || !sessionExpiry.expiresAt || sessionExpiry.expiresAt <= new Date().toISOString()) {
        await this.signOut();
        throw new Error('Failed to retrieve session expiry.');
      }
      return sessionExpiry;
    },
  },
  persist: {
    storage: sessionStorage,
  },
});
